import { ArrowLeft, ArrowRight, CheckCircle2, ChevronRight, Clock3, Lightbulb, ListChecks, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CodeBlock } from "../components/CodeBlock";
import { EmptyState } from "../components/EmptyState";
import { LoadingState } from "../components/LoadingState";
import { Quiz } from "../components/Quiz";
import { VideoPlayer } from "../components/VideoPlayer";
import { useToast } from "../context/ToastContext";
import { ApiError, api, postJson } from "../lib/api";
import type { LessonDetail } from "../types/api";

export function LessonPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [lesson, setLesson] = useState<LessonDetail | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const loadLesson = useCallback(async () => {
    if (!lessonId) return;
    setLesson(null);
    setError(null);
    try {
      const data = await api<LessonDetail>(`/api/lessons/${lessonId}`);
      setLesson(data);
      void postJson(`/api/lessons/${lessonId}/visit`);
      window.scrollTo({ top: 0, behavior: "instant" });
    } catch (caught) {
      setError({ status: caught instanceof ApiError ? caught.status : 500, message: caught instanceof Error ? caught.message : "Lesson unavailable." });
    }
  }, [lessonId]);

  useEffect(() => { void loadLesson(); }, [loadLesson]);

  async function markComplete() {
    if (!lesson) return;
    setSaving(true);
    try {
      await postJson(`/api/lessons/${lesson.id}/complete`);
      setLesson({ ...lesson, completed: true });
      showToast("Lesson marked complete.");
    } catch (caught) {
      showToast(caught instanceof Error ? caught.message : "Progress could not be saved.", "error");
    } finally {
      setSaving(false);
    }
  }

  if (error) return <EmptyState title={error.status === 403 ? "This lesson is locked" : "Lesson unavailable"} message={error.message} action={<Link className="button button-primary" to={error.status === 403 ? "/#plans" : "/dashboard"}>{error.status === 403 ? "View upgrade" : "Back to dashboard"}</Link>} />;
  if (!lesson) return <LoadingState label="Opening lesson…" />;

  return (
    <article className="lesson-page page-enter">
      <header className="lesson-header">
        <div className="breadcrumbs"><Link to="/dashboard">Course</Link><ChevronRight size={14} /><span>Programmer {lesson.tier}</span><ChevronRight size={14} /><span>{lesson.title}</span></div>
        <div className="lesson-heading-row">
          <div><span className={`course-kicker tier-text-${lesson.tier}`}>PROGRAMMER {lesson.tier} · LESSON {String(lesson.order).padStart(2, "0")}</span><h1>{lesson.title}</h1><p>{lesson.intro}</p><div className="lesson-meta"><span><Clock3 size={15} />{lesson.duration}</span><span><ListChecks size={15} />{lesson.quiz.questions.length} question test</span></div></div>
          {lesson.completed && <span className="completed-pill"><CheckCircle2 />Completed</span>}
        </div>
      </header>

      <VideoPlayer youtubeId={lesson.youtubeId} title={`${lesson.title} video lesson`} />

      <div className="lesson-content">
        {lesson.sections.map((section) => <section key={section.heading} className={`lesson-section lesson-tone-${section.tone || "default"}`}>
          {section.tone === "tip" && <span className="callout-icon"><Lightbulb /></span>}
          {section.tone === "warning" && <span className="callout-icon"><TriangleAlert /></span>}
          <div><h2>{section.heading}</h2>{section.body && <p>{section.body}</p>}{section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}</div>
        </section>)}
        {lesson.codeExamples.length > 0 && <section className="code-examples"><div className="content-heading"><span className="eyebrow">Try it in Studio</span><h2>Example code</h2><p>Read the code first, then copy it into the location shown.</p></div>{lesson.codeExamples.map((example, index) => <CodeBlock key={`${example.title}-${index}`} example={example} />)}</section>}
      </div>

      <Quiz lesson={lesson} onCompleted={() => setLesson((current) => current ? { ...current, completed: true } : current)} />

      <nav className="lesson-footer-nav" aria-label="Lesson navigation">
        {lesson.previousLesson ? <Link to={`/learn/${lesson.previousLesson.id}`}><ArrowLeft /><span><small>Previous lesson</small><strong>{lesson.previousLesson.title}</strong></span></Link> : <span />}
        {!lesson.completed && <button className="button button-secondary" onClick={markComplete} disabled={saving}><CheckCircle2 size={17} />{saving ? "Saving…" : "Mark complete"}</button>}
        {lesson.nextLesson ? <Link className="next-lesson" to={`/learn/${lesson.nextLesson.id}`}><span><small>Next lesson</small><strong>{lesson.nextLesson.title}</strong></span><ArrowRight /></Link> : <Link className="next-lesson" to="/dashboard"><span><small>Course</small><strong>Back to dashboard</strong></span><ArrowRight /></Link>}
      </nav>
    </article>
  );
}
